import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, Card, Checkbox, Drawer, Field, Input, Kpi, KpiStrip, Pill, SegmentedControl, Switch, TableFooter, TableHead, TableRow, useToast, type PillTone } from '@os/ui';
import { api, useApiMutation, useApps, useUsers, type User } from '../../api';
import { initials } from '../../desktop/items';
import { Page, PageAlert, PageEmpty } from '../../desktop/Page';
import './admin.css';

type RoleMap = Record<string, string[]>;
type Filter = 'all' | 'admins' | 'nomfa' | 'disabled';
const COLS = 'minmax(220px, 1.3fr) minmax(220px, 2fr) 80px 170px 130px';

/** Users & roles: KPI strip, filter tabs + search, full-width table; edit / create in a drawer. */
export default function Users() {
  const users = useUsers();
  const apps = useApps();
  const toast = useToast((s) => s.show);
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const err = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong', 'danger');
  const patch = useApiMutation(({ id, body }: { id: string; body: Record<string, unknown> }) => api(`/admin/users/${id}`, { method: 'PATCH', body }), [['admin', 'users']]);
  const create = useApiMutation((body: Record<string, unknown>) => api('/admin/users', { method: 'POST', body }), [['admin', 'users']]);

  const list = users.data ?? [];
  const admins = list.filter((u) => (u.roles.os?.length ?? 0) > 0);
  const noMfa = list.filter((u) => !u.mfa_enabled);
  const disabled = list.filter((u) => !u.active);
  const byFilter: Record<Filter, User[]> = { all: list, admins, nomfa: noMfa, disabled };
  const needle = q.trim().toLowerCase();
  const shown = byFilter[filter].filter((u) => !needle || `${u.display_name} ${u.email}`.toLowerCase().includes(needle));

  return (
    <Page title="Users & roles" subtitle="Everyone who can sign in to this OS and what they may open" actions={<Button variant="primary" icon={<Plus size={15} strokeWidth={1.5} />} onClick={() => setCreating(true)}>New user</Button>}>
      <PageAlert error={users.error} fallback="Could not load users" />

      <KpiStrip columns={4}>
        <Kpi label="Accounts" value={users.data ? list.length : '…'} />
        <Kpi label="OS admins" value={users.data ? admins.length : '…'} sub="Manage users and apps" />
        <Kpi label="Without MFA" value={users.data ? noMfa.length : '…'} tone={noMfa.length > 0 ? 'danger' : 'muted'} sub="Required in production" />
        <Kpi label="Disabled" value={users.data ? disabled.length : '…'} tone="muted" />
      </KpiStrip>

      <Card>
        <div className="pg-toolbar">
          <SegmentedControl variant="tabs" value={filter} onChange={setFilter} segments={[
            { value: 'all', label: 'All', count: list.length },
            { value: 'admins', label: 'Admins', count: admins.length },
            { value: 'nomfa', label: 'No MFA', count: noMfa.length },
            { value: 'disabled', label: 'Disabled', count: disabled.length },
          ]} />
          <Input compact className="pg-search" placeholder="Search name or email" aria-label="Search users" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="pg-scroll">
          <div>
            <TableHead cols={COLS} columns={[{ label: 'User' }, { label: 'Roles' }, { label: 'MFA' }, { label: 'Last login' }, { label: '', align: 'right' }]} />
            {shown.map((u) => (
              <TableRow key={u.id} cols={COLS} onClick={() => setEditing(u)} dim={!u.active}>
                <div className="pg-user">
                  <span className="dk-avatar" aria-hidden>{initials(u.display_name)}</span>
                  <span style={{ minWidth: 0 }}><b className="truncate">{u.display_name}</b><small className="truncate">{u.email}</small></span>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {Object.entries(u.roles).map(([a, rs]) => <Pill key={a} small tone={(a === 'os' ? 'danger' : 'outline') as PillTone}>{a}: {rs.join(', ')}</Pill>)}
                  {Object.keys(u.roles).length === 0 && <span className="meta-sm">No roles</span>}
                </div>
                <div>{u.mfa_enabled ? <Pill tone="ok" small>on</Pill> : <Pill tone="warn" small>off</Pill>}</div>
                <div className="meta-sm tabular">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : 'Never'}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                  {!u.active && <Pill tone="danger" small>disabled</Pill>}
                  <Button size="row" onClick={(e) => { e.stopPropagation(); setEditing(u); }}>Edit</Button>
                </div>
              </TableRow>
            ))}
            {users.data && shown.length === 0 && <PageEmpty>{list.length === 0 ? 'No users yet.' : 'No users match this filter.'}</PageEmpty>}
          </div>
        </div>
        <TableFooter><span>{shown.length} of {list.length} accounts</span><span>Click a row to edit roles</span></TableFooter>
      </Card>

      {editing && (
        <RoleDrawer key={editing.id} title={editing.display_name} user={editing} apps={apps.data ?? []} saving={patch.isPending} onClose={() => setEditing(null)}
          onSave={(body) => patch.mutate({ id: editing.id, body }, { onSuccess: () => { toast('Saved'); setEditing(null); }, onError: err })} />
      )}
      {creating && (
        <RoleDrawer title="New user" apps={apps.data ?? []} saving={create.isPending} onClose={() => setCreating(false)}
          onSave={(body) => create.mutate(body, { onSuccess: () => { toast('User created'); setCreating(false); }, onError: err })} />
      )}
    </Page>
  );
}

/** Keyed by `user.id` from the caller so switching rows resets the form state. */
function RoleDrawer({ title, user, apps, saving = false, onClose, onSave }: { title: string; user?: User; apps: { id: string; name: string; roles: string[] }[]; saving?: boolean; onClose: () => void; onSave: (body: Record<string, unknown>) => void }) {
  const [name, setName] = useState(user?.display_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(user?.active ?? true);
  const [resetMfa, setResetMfa] = useState(false);
  const [roles, setRoles] = useState<RoleMap>(user?.roles ?? {});
  const toggle = (app: string, role: string) => setRoles((r) => { const cur = new Set(r[app] ?? []); if (cur.has(role)) cur.delete(role); else cur.add(role); const next = { ...r, [app]: [...cur] }; if (next[app]!.length === 0) delete next[app]; return next; });
  const body = user
    ? { display_name: name, active, roles, ...(resetMfa && { reset_mfa: true }), ...(password && { new_password: password }) }
    : { display_name: name, email, ...(password && { password }), roles };
  const pwInvalid = password.length > 0 && password.length < 10;
  return (
    <Drawer open title={title} onClose={onClose} footer={<><Button size="form" onClick={onClose} disabled={saving}>Cancel</Button><Button variant="primary" size="form" onClick={() => onSave(body)} loading={saving} disabled={!name || pwInvalid || (!user && !email) || saving}>{user ? 'Save' : 'Create'}</Button></>}>
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Display name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        {!user && <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>}
        <Field label={user ? 'Set new password (optional, min 10)' : 'Initial password (min 10; leave empty for social sign-in only)'} hint={!user && !password ? 'Without a password the user signs in with Google, Microsoft or LinkedIn using this email.' : undefined}><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {user && (
          <div style={{ display: 'flex', gap: 18 }}>
            <Switch checked={active} onChange={setActive} label="Account active" />
            <Checkbox checked={resetMfa} onChange={(e) => setResetMfa(e.target.checked)} label="Reset MFA" />
          </div>
        )}
        <div>
          <div className="kicker" style={{ marginBottom: 8 }}>Roles per app</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {apps.map((a) => (
              <div key={a.id} style={{ border: '1px solid var(--border)', borderRadius: 4, padding: '10px 12px' }}>
                <div style={{ fontWeight: 500, marginBottom: 6 }}>{a.name} <span className="meta-sm">({a.id})</span></div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>{a.roles.map((r) => <Checkbox key={r} checked={roles[a.id]?.includes(r) ?? false} onChange={() => toggle(a.id, r)} label={r} />)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
